import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface Link {
  link: string;
  team: string;
  status: 'ACTIVE' | 'INACTIVE';
  password: string;
  username?: string;
  deleted: boolean;
  hacker_name?: string;
}

interface EditUrlDialogProps {
  isOpen: boolean;
  onClose: () => void;
  linkToEdit: Link;
  isLoading: boolean;
  onConfirm: (updatedLink: Link) => void;
}

const EditUrlDialog: React.FC<EditUrlDialogProps> = ({ isOpen, onClose, linkToEdit, isLoading, onConfirm }) => {
  const [formData, setFormData] = useState<Link>({ ...linkToEdit });
  const [errors, setErrors] = useState<Partial<Record<keyof Link, string>>>({});

  const handleChange = (field: keyof Link, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: field === 'status' ? (value as 'ACTIVE' | 'INACTIVE') : value,
    }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof Link, string>> = {};

    if (!formData.link) {
      newErrors.link = 'URL is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!formData.team) {
      newErrors.team = 'Team is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onConfirm(formData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='text-xl'>Edit Shell URL</DialogTitle>
          <DialogDescription>Update the shell URL details. All fields are required.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className='space-y-4 pt-2'>
          <div className='space-y-1'>
            <Label htmlFor='link'>Shell URL</Label>
            <Input
              id='link'
              value={formData.link}
              onChange={e => handleChange('link', e.target.value)}
              placeholder='Enter URL'
              disabled={isLoading}
              className={errors.link ? 'border-red-500' : ''}
            />
            {errors.link && <p className='text-sm text-red-500'>{errors.link}</p>}
          </div>

          <div className='grid grid-cols-3 gap-4'>
            <div className='space-y-1'>
              <Label htmlFor='team'>Team</Label>
              <Input
                id='team'
                value={formData.team}
                onChange={e => handleChange('team', e.target.value)}
                placeholder='Enter team'
                disabled={isLoading}
                className={errors.team ? 'border-red-500' : ''}
              />
              {errors.team && <p className='text-sm text-red-500'>{errors.team}</p>}
            </div>

            <div className='space-y-1'>
              <Label htmlFor='password'>Password</Label>
              <Input
                id='password'
                value={formData.password}
                onChange={e => handleChange('password', e.target.value)}
                placeholder='Enter password'
                disabled={isLoading}
                className={errors.password ? 'border-red-500' : ''}
              />
              {errors.password && <p className='text-sm text-red-500'>{errors.password}</p>}
            </div>

            <div className='space-y-1'>
              <Label htmlFor='username'>Username</Label>
              <Input
                id='username'
                value={formData.username || ''}
                onChange={e => handleChange('username', e.target.value)}
                placeholder='Enter username'
                disabled={isLoading}
                className={errors.username ? 'border-red-500' : ''}
              />
              {errors.username && <p className='text-sm text-red-500'>{errors.username}</p>}
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='status'>Status</Label>
            <Select value={formData.status} onValueChange={value => handleChange('status', value)} disabled={isLoading}>
              <SelectTrigger id='status' className='w-full'>
                <SelectValue placeholder='Select status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='ACTIVE'>Active</SelectItem>
                <SelectItem value='INACTIVE'>Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='hacker_name'>Hacker Name</Label>
            <Input
              id='hacker_name'
              value={formData.hacker_name}
              onChange={e => handleChange('hacker_name', e.target.value)}
              placeholder='Enter hacker name'
              disabled={isLoading}
              className={errors.hacker_name ? 'border-red-500' : ''}
            />
            {errors.hacker_name && <p className='text-sm text-red-500'>{errors.hacker_name}</p>}
          </div>

          <DialogFooter className='pt-4'>
            <Button type='button' variant='outline' onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type='submit' disabled={isLoading} className='ml-2'>
              {isLoading ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Updating...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditUrlDialog;
